/**
 * CoPilot Context Provider
 *
 * Full CoPilotPageContext management: builds context from router + journey instance + page-level state.
 * Replaces the minimal CoPilotSidebarProvider with a richer context that includes
 * sidebar state, page context, host interface, and event bus.
 *
 * Backward-compatible: useCoPilotSidebar() still works.
 */

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'wouter';
import { useLocale } from '@tonnie/locale';
import type {
  CoPilotPageContext,
  CoPilotHostCommand,
  CoPilotHostEvent,
  CoPilotHostOptions,
  CoPilotHostInterface,
  CoPilotMode,
} from '@tonnie/copilot';
import { copilotDomainForPath } from '@tonnie/copilot/domains';
import { useJourneyInstance } from './JourneyInstanceContext';
import { useCoPilotSidebar as useOriginalSidebar } from './CoPilotSidebarContext';

// ═══════════════════════════════════════════════════════════════════════════
// CONTEXT TYPES
// ═══════════════════════════════════════════════════════════════════════════

interface CoPilotContextValue {
  // Sidebar state (backward-compatible)
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  toggle: () => void;

  // Full page context
  pageContext: CoPilotPageContext;

  // Host interface registration
  registerHost: (options: CoPilotHostOptions) => void;
  unregisterHost: () => void;

  // Event bus
  emit: (event: CoPilotHostEvent) => void;
  flushPendingEvents: () => CoPilotHostEvent[];
  onCommand: (cmd: CoPilotHostCommand) => void;

  // A question asked from the page, for the engine to send once open
  askedQuestion: string | null;
  ask: (question: string) => void;
  takeAskedQuestion: () => void;

  // Active mode
  activeMode: CoPilotMode | null;
  setActiveMode: (mode: CoPilotMode | null) => void;

  // Active sessions
  activeHybridSessionId: string | null;
  setActiveHybridSessionId: (id: string | null) => void;
  activeGuideFlowId: string | null;
  setActiveGuideFlowId: (id: string | null) => void;
}

const CoPilotContext = createContext<CoPilotContextValue | undefined>(undefined);

// ═══════════════════════════════════════════════════════════════════════════
// PROVIDER
// ═══════════════════════════════════════════════════════════════════════════

export function CoPilotContextProvider({ children }: { children: ReactNode }) {
  const sidebar = useOriginalSidebar();
  const isOpen = sidebar.isOpen;
  const setIsOpen = sidebar.setIsOpen;
  const [activeMode, setActiveMode] = useState<CoPilotMode | null>(null);
  const [activeHybridSessionId, setActiveHybridSessionId] = useState<string | null>(null);
  const [activeGuideFlowId, setActiveGuideFlowId] = useState<string | null>(null);
  const [askedQuestion, setAskedQuestion] = useState<string | null>(null);

  const [location] = useLocation();
  const { locale } = useLocale();
  const { activeInstance } = useJourneyInstance();

  const hostOptionsRef = useRef<CoPilotHostOptions | null>(null);
  const commandHandlerRef = useRef<((cmd: CoPilotHostCommand) => void) | null>(null);
  const pendingEventsRef = useRef<CoPilotHostEvent[]>([]);

  const toggle = useCallback(() => sidebar.toggle(), [sidebar]);

  const pageContext = useMemo<CoPilotPageContext>(() => {
    // The page's CoPilot domain, from the one registry every layer validates
    // against; undefined on a page in no domain (the turn runs as `general`).
    const hero = copilotDomainForPath(location);

    return {
      portal: 'journey',
      hero,
      screen: location,
      journeyInstanceId: activeInstance?.id ?? null,
      entityId: hostOptionsRef.current?.entityId ?? null,
      entityType: (hostOptionsRef.current?.entityType as CoPilotPageContext['entityType']) ?? null,
      entityState: hostOptionsRef.current?.entityState ?? null,
      availableActions: hostOptionsRef.current?.availableActions ?? ['navigate', 'dismiss'],
      urgency: 'normal',
      locale: locale ?? 'en',
      metadata: hostOptionsRef.current?.metadata ?? null,
    };
  }, [location, activeInstance?.id, locale]);

  const registerHost = useCallback((options: CoPilotHostOptions) => {
    hostOptionsRef.current = options;
    commandHandlerRef.current = options.onCommand;
  }, []);

  const unregisterHost = useCallback(() => {
    hostOptionsRef.current = null;
    commandHandlerRef.current = null;
  }, []);

  const emit = useCallback((event: CoPilotHostEvent) => {
    pendingEventsRef.current.push(event);
  }, []);

  const flushPendingEvents = useCallback((): CoPilotHostEvent[] => {
    const events = pendingEventsRef.current;
    pendingEventsRef.current = [];
    return events;
  }, []);

  /** Opens the panel with a question of the page's — the engine sends it (`CoPilotEngineProvider`). */
  const ask = useCallback(
    (question: string) => {
      setAskedQuestion(question);
      setIsOpen(true);
    },
    [setIsOpen]
  );
  const takeAskedQuestion = useCallback(() => setAskedQuestion(null), []);

  const onCommand = useCallback((cmd: CoPilotHostCommand) => {
    if (commandHandlerRef.current) {
      commandHandlerRef.current(cmd);
    }
  }, []);

  const value = useMemo<CoPilotContextValue>(
    () => ({
      isOpen,
      setIsOpen,
      toggle,
      pageContext,
      registerHost,
      unregisterHost,
      emit,
      flushPendingEvents,
      onCommand,
      askedQuestion,
      ask,
      takeAskedQuestion,
      activeMode,
      setActiveMode,
      activeHybridSessionId,
      setActiveHybridSessionId,
      activeGuideFlowId,
      setActiveGuideFlowId,
    }),
    [
      isOpen,
      setIsOpen,
      toggle,
      pageContext,
      registerHost,
      unregisterHost,
      emit,
      flushPendingEvents,
      onCommand,
      askedQuestion,
      ask,
      takeAskedQuestion,
      activeMode,
      activeHybridSessionId,
      activeGuideFlowId,
    ]
  );

  return <CoPilotContext.Provider value={value}>{children}</CoPilotContext.Provider>;
}

// ═══════════════════════════════════════════════════════════════════════════
// HOOKS
// ═══════════════════════════════════════════════════════════════════════════

function useCoPilotCtx(): CoPilotContextValue {
  const context = useContext(CoPilotContext);
  if (!context) {
    throw new Error('useCoPilotContext must be used within CoPilotContextProvider');
  }
  return context;
}

/**
 * Backward-compatible sidebar hook.
 */
export function useCoPilotSidebar() {
  const { isOpen, setIsOpen, toggle } = useCoPilotCtx();
  return { isOpen, setIsOpen, toggle };
}

/**
 * Full CoPilot page context.
 */
export function useCoPilotContext() {
  const ctx = useCoPilotCtx();
  return {
    pageContext: ctx.pageContext,
    activeMode: ctx.activeMode,
    activeHybridSessionId: ctx.activeHybridSessionId,
    activeGuideFlowId: ctx.activeGuideFlowId,
    emit: ctx.emit,
    flushPendingEvents: ctx.flushPendingEvents,
    askedQuestion: ctx.askedQuestion,
    takeAskedQuestion: ctx.takeAskedQuestion,
  };
}

/**
 * Ask the CoPilot from the page — "Ask the CoPilot" on a card: the panel opens
 * and the question is the person's turn once the conversation is read.
 */
export function useAskCoPilot(): (question: string) => void {
  return useCoPilotCtx().ask;
}

/**
 * Host interface hook — registers page capabilities with CoPilot.
 */
export function useCoPilotHost(options: CoPilotHostOptions): CoPilotHostInterface {
  const ctx = useCoPilotCtx();
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    ctx.registerHost(optionsRef.current);
    return () => ctx.unregisterHost();
  }, [ctx]);

  return {
    emit: ctx.emit,
    context: ctx.pageContext,
    isConnected: ctx.isOpen,
    openCoPilot: (prompt?: string) => (prompt ? ctx.ask(prompt) : ctx.setIsOpen(true)),
    closeCoPilot: () => ctx.setIsOpen(false),
  };
}
